import { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { StaffForm } from '../../../schemas'
import { useShiftEditor } from '../../../useShiftEditor'
import { EditorMode, ShiftType } from '../../../ShiftEditorProvider'
import {
  Box,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Paragraph,
  Select,
} from 'ui'
import { ShiftLeadInfoModal } from './ShiftLeadInfoModal'
import {
  DEFAULT_SHIFT_LEAD_EXTRA_TIME_DURATION,
  SHIFT_LEAD_EXTRA_TIME_DURATION,
} from 'lib/time'
import { useGateValue } from '@statsig/react-bindings'
import { DynamicConfigName, FeatureGates } from 'lib/statsig/feature_gates'
import { useIsCompanyWhitelisted } from 'hooks/useIsCompanyWhitelisted'
import { useShiftLeadMinWorkers } from 'hooks/useShiftLeadMinWorkers'
import { useAuthContext } from 'components/auth'
import { useShiftLeadEligibility } from 'queries/shift'

export function ShiftLeads() {
  const { isNew, editorMode, shiftType, workerToShiftLeadRatio } =
    useShiftEditor()
  const {
    watch,
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<StaffForm>()
  const numWorkers = watch('numWorkers') ?? 0
  const numShiftLeads = watch('numShiftLeads') ?? 0
  const isLeadShiftCreationEnabled = useIsCompanyWhitelisted(
    DynamicConfigName.ShiftLeadCreationHospitality
  )
  const shiftLeadMinWorkers = useShiftLeadMinWorkers()
  const shouldShowShiftLeads = useMemo(() => {
    return (
      isNew &&
      editorMode === EditorMode.Standard &&
      (shiftType === ShiftType.Regular ||
        shiftType === ShiftType.LongTermAssignment) &&
      numWorkers &&
      Number(numWorkers) >= shiftLeadMinWorkers &&
      isLeadShiftCreationEnabled
    )
  }, [
    isNew,
    numWorkers,
    editorMode,
    shiftType,
    isLeadShiftCreationEnabled,
    shiftLeadMinWorkers,
  ])

  const { company } = useAuthContext()
  const { data: shiftLeadEligibility } = useShiftLeadEligibility(
    { companyId: company?.id },
    { enabled: !!shouldShowShiftLeads }
  )
  const cannotCreateShiftLeads =
    shiftLeadEligibility?.canCreateShiftLeads === false
  const isW2LeadPositionsEnabled = useGateValue(
    FeatureGates.EnableW2LeadPositions
  )
  const shouldShowLeadShiftsInfo =
    isW2LeadPositionsEnabled &&
    shiftLeadEligibility?.canCreateShiftLeads === true

  useEffect(() => {
    if (cannotCreateShiftLeads) {
      setValue('numShiftLeads', undefined)
    }
  }, [cannotCreateShiftLeads, setValue])

  if (!shouldShowShiftLeads) {
    return null
  }

  return (
    <Box mt={5}>
      <Heading level={3}>Shift Leads</Heading>
      <Paragraph>
        Shift Leads are experienced workers who can help manage the team. We
        recommend having one Shift Lead for every {workerToShiftLeadRatio}{' '}
        workers. <ShiftLeadInfoModal />
      </Paragraph>
      <Field
        label={'Number of Shift Leads'}
        error={errors.numShiftLeads?.message?.toString()}
      >
        <Input
          {...register('numShiftLeads')}
          disabled={cannotCreateShiftLeads}
          aria-label={'Number of Shift Leads'}
          placeholder={`e.g. ${Math.ceil(
            Number(numWorkers) / workerToShiftLeadRatio
          )}`}
        />
      </Field>
      {cannotCreateShiftLeads ? (
        <Message variant="error" systemLevel>
          Shift leads can't be created. This business needs a linked W2 company.
          Please contact support to set it up.
        </Message>
      ) : null}
      {shouldShowLeadShiftsInfo ? (
        <Message variant="info" systemLevel>
          We'll create shifts for the leads automatically. Once created, they'll
          appear as linked on the shift details page.
        </Message>
      ) : null}
      {numShiftLeads > 0 ? (
        <Flex flexDirection={['column', 'row']}>
          <Field
            width={[1, 1 / 2]}
            mr={[0, 2]}
            label={'Early clock in before shift starts'}
            error={errors.shiftLeadEarlyStartTime?.message?.toString()}
          >
            <Controller
              name={'shiftLeadEarlyStartTime'}
              control={control}
              render={({ field }) => (
                <Select
                  isMulti={false}
                  aria-label={'Select Early Start Time'}
                  placeholder={'Select a start time'}
                  defaultValue={DEFAULT_SHIFT_LEAD_EXTRA_TIME_DURATION}
                  value={field.value}
                  options={SHIFT_LEAD_EXTRA_TIME_DURATION}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
          <Field
            width={[1, 1 / 2]}
            ml={[0, 2]}
            label={'Late clock out after shift ends'}
            error={errors.shiftLeadLateEndTime?.message?.toString()}
          >
            <Controller
              name={'shiftLeadLateEndTime'}
              control={control}
              render={({ field }) => (
                <Select
                  isMulti={false}
                  aria-label={'Select Late End Time'}
                  placeholder={'Select an end time'}
                  defaultValue={DEFAULT_SHIFT_LEAD_EXTRA_TIME_DURATION}
                  value={field.value}
                  options={SHIFT_LEAD_EXTRA_TIME_DURATION}
                  onChange={field.onChange}
                />
              )}
            />
          </Field>
        </Flex>
      ) : null}
    </Box>
  )
}
